import {
  UserDto,
} from '@act/shared/data-transfer-objects';
import { Dictionary } from '@act/shared/types';
import {
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { addToEffectsMiddleware } from 'redux/utils/effectsMiddleware.util';
import { userRequests } from '../requests/users.request';
import { useAppSelector } from '../storeHooks';
import { UserState } from '../types/usersTypes';
import { createApiThunk } from '../utils/apiThunk';
import { dedupeUserDtosById } from '../utils/dedupeUserDtosById.util';
import { verifyUser } from './actingUserSlice.slice';
import { UserRole, userIsMember } from '@act/shared/definitions';
import { getSMSMessages } from './smsMessagesSlice.slice';
import { resetRedux } from '../actions/sharedActions';
// Import shared thunks to avoid circular dependency
import { getCalls } from '../thunks/calls.thunk';
import { getUsersByIds } from '../thunks/users.thunk';
export { getUsersByIds };

// THUNKS/ACTIONS ==============================================================
export const getTeam = createApiThunk(userRequests.getTeam);
const getHierarchy = createApiThunk(userRequests.getHierarchy);
export const loadTeamHierarchy = createAction('users/loadHierarchy');
export const getTeams = createApiThunk(userRequests.getTeams);

// INITIAL STATE ===============================================================
const userEntityAdapter = createEntityAdapter<UserDto>();
const initialState: UserState = {
  actingUserId: null,
  membersLoadingStatus: 'initial',
  teamLoadingStatus: 'initial',
  teamHierarchy: null,
  viewable: null,
  callInviteTeamUsers: null,
  hierarchyLoadingStatus: 'initial',
  users: userEntityAdapter.getInitialState(),
  allTeams: null,
  allTeamsLoadingStatus: 'initial',
};

const addNonMembersOnly = (state: UserState, users: UserDto[]) => {
  const toAdd = [];
  for (const user of users) {
    if (!userIsMember(user)) {
      toAdd.push(user);
    }
  }
  userEntityAdapter.addMany(state.users, toAdd);
};

// SLICE =======================================================================
export const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    userAuthenticated: (_state: UserState, _action): void => {
      //TODO
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.actingUserId = action.payload.user.id;
        userEntityAdapter.addOne(state.users, action.payload.user);
      })
      .addCase(getTeam.pending, state => {
        state.teamLoadingStatus = 'loading';
      })
      .addCase(getTeam.fulfilled, (state, action) => {
        const teamUsers = dedupeUserDtosById(action.payload);
        const params = action.meta.arg?.params;
        const isCallInviteTeamFetch =
          params?.allViewable === true && params?.allCareStaff === true;
        if (isCallInviteTeamFetch) {
          state.callInviteTeamUsers = teamUsers;
        } else {
          state.viewable = teamUsers;
        }
        userEntityAdapter.upsertMany(state.users, teamUsers);
        state.teamLoadingStatus = 'success';
      })
      .addCase(getTeam.rejected, state => {
        state.teamLoadingStatus = 'failed';
      })
      .addCase(getHierarchy.pending, (state: UserState) => {
        state.hierarchyLoadingStatus = 'loading';
      })
      .addCase(getHierarchy.fulfilled, (state: UserState, action) => {
        state.hierarchyLoadingStatus = 'success';
        state.teamHierarchy = action.payload;
      })
      .addCase(getHierarchy.rejected, (state: UserState) => {
        state.hierarchyLoadingStatus = 'failed';
      })
      .addCase(getUsersByIds.fulfilled, (state, action) => {
        addNonMembersOnly(state, action.payload.users);
      })
      .addCase(getCalls.fulfilled, (state, action) => {
        addNonMembersOnly(state, action.payload.users);
      })
      .addCase(getSMSMessages.fulfilled, (state, action) => {
        addNonMembersOnly(state, action.payload.users);
      })
      .addCase(getTeams.pending, state => {
        state.allTeamsLoadingStatus = 'loading';
      })
      .addCase(getTeams.fulfilled, (state, action) => {
        state.allTeams = action.payload;
        state.allTeamsLoadingStatus = 'success';
      })
      .addCase(getTeams.rejected, state => {
        state.allTeamsLoadingStatus = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const usersReducer = usersSlice.reducer;

// SELECTORS ===================================================================
const selectUsersSliceState = state =>
  (state as { users: typeof initialState })?.users;

export const selectUsersState = state =>
  (state as { users: typeof initialState })?.users?.users;

export const selectHierarchy = createSelector(
  selectUsersSliceState,
  s => s.teamHierarchy
);

export const selectAllTeams = createSelector(
  selectUsersSliceState,
  s => s.allTeams
);

export const selectViewable = createSelector(
  selectUsersSliceState,
  s => s.viewable
);

export const selectMembersLoadingStatus = createSelector(
  selectUsersSliceState,
  userState => userState.membersLoadingStatus
);
export const selectTeamLoadingStatus = createSelector(
  selectUsersSliceState,
  userState => userState.teamLoadingStatus
);
export const selectHierarchyLoadingStatus = createSelector(
  selectUsersSliceState,
  userState => userState.hierarchyLoadingStatus
);

export const { selectAll: selectUsers, selectEntities: selectUsersMap } =
  userEntityAdapter.getSelectors(selectUsersState);

const selectCurrentGuidesDictionary = createSelector(selectUsersMap, all => {
  const guides = Object.values(all).filter(u => u.isDeleted === false);
  const dictionary: Dictionary<UserDto> = {};

  guides.forEach(g => {
    dictionary[g.id] = g;
  });
  return dictionary;
});

const selectCurrentGuides = createSelector(
  selectCurrentGuidesDictionary,
  guides => {
    const guideArray = Object.values(guides);
    guideArray.sort((a, b) => a.name.localeCompare(b.name));

    return guideArray;
  }
);

const selectCallInviteTeamUsersState = createSelector(
  selectUsersSliceState,
  s => s.callInviteTeamUsers
);

/** Call add-participant dropdown (`getTeam` with allViewable + allCareStaff). */
const selectCallInviteTeamUsers = createSelector(
  selectCallInviteTeamUsersState,
  callInviteTeamUsers => {
    if (callInviteTeamUsers === null) {
      return null;
    }
    const guides = dedupeUserDtosById(callInviteTeamUsers).filter(
      u => u.isDeleted === false && u.role !== UserRole.SYSTEM
    );
    return [...guides].sort((a, b) => a.name.localeCompare(b.name));
  }
);

export const selectMembers = createSelector(selectUsers, all =>
  all.filter(u => userIsMember(u))
);

const selectUserById = (id: string) =>
  createSelector(selectUsersState, users => {
    return users.entities[id];
  });

const selectUsersMapByInteractableId = createSelector(selectUsers, allUsers => {
  const map: { [key: string]: UserDto } = {};
  allUsers.forEach(u => {
    map[u.interactableId] = u;
  });
  return map;
});

const selectActingUser = state => {
  const usersState = selectUsersSliceState(state);
  return usersState.users.entities[usersState.actingUserId];
};

// EXPORT HELPER ===============================================================
export const userAppSelectors = {
  useSelectActingUser: () => useAppSelector(selectActingUser),
  useSelectUsers: () => useAppSelector(selectUsers),
  useSelectUserById: id => useAppSelector(selectUserById(id)),
  useSelectUsersMap: () => useAppSelector(selectUsersMap),
  useSelectCurrentGuidesMap: () =>
    useAppSelector(selectCurrentGuidesDictionary),
  useSelectCurrentGuides: () => useAppSelector(selectCurrentGuides),
  useSelectCallInviteTeamUsers: () =>
    useAppSelector(selectCallInviteTeamUsers),
  useSelectUserByInteractableId: (id: string) =>
    useAppSelector(state => selectUsersMapByInteractableId(state)[id]),
  useSelectAllTeams: () => useAppSelector(selectAllTeams),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: usersSlice.actions.userAuthenticated,
    effect: async ({ dispatch }) => {
      void dispatch(getTeam({}));
    },
  });
  addEffect({
    action: loadTeamHierarchy,
    effect: ({ dispatch, state }) => {
      const loadingStatus = selectUsersSliceState(state).hierarchyLoadingStatus;
      if (loadingStatus === 'initial' || loadingStatus === 'failed') {
        dispatch(getHierarchy({}));
      }
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
